/**
 * Multi-select chip group for finite-option fields (vibe preferences,
 * categories to avoid, desired categories). Different from ChipInput
 * because the option set is fixed, the user picks from known options,
 * doesn't type new ones.
 *
 * Pioneer alignment:
 * - Nielsen #6: recognition (see the options, click to toggle)
 *   beats recall (type from memory)
 * - Wroblewski: forms with finite options should look like multi-select
 *   chips, not dropdowns. Faster to scan, faster to commit.
 * - Stanley: each chip is keyboard-focusable + screen-reader-announced
 */

interface Option {
  value: string;
  label: string;
}

interface MultiSelectChipsProps {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
  hint?: string;
  /** Visual treatment, "selected" gets coral fill (positive), "avoid" gets a different visual to signal "do not". */
  treatment?: "select" | "avoid";
}

export function MultiSelectChips({
  label,
  options,
  selected,
  onChange,
  hint,
  treatment = "select",
}: MultiSelectChipsProps) {
  const selectedSet = new Set(selected);

  const toggle = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-2">
      <label
        className="block text-sm font-semibold text-navy"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        {label}
      </label>

      <div
        className="flex flex-wrap gap-2"
        role="group"
        aria-label={label}
      >
        {options.map((opt) => {
          const isSelected = selectedSet.has(opt.value);
          const baseClasses =
            "rounded-full border-2 px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-coral";

          let stateClasses = "";
          if (treatment === "select") {
            stateClasses = isSelected
              ? "border-navy bg-navy text-white"
              : "border-navy/20 bg-white text-navy/75 hover:border-navy/50 hover:text-navy";
          } else {
            // "avoid" treatment, when selected, signals "no" via coral outline
            stateClasses = isSelected
              ? "border-coral bg-coral/10 text-coral"
              : "border-navy/20 bg-white text-navy/75 hover:border-coral/50 hover:text-coral";
          }

          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              aria-pressed={isSelected}
              className={`${baseClasses} ${stateClasses}`}
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {treatment === "avoid" && isSelected && (
                <span aria-hidden="true">✕ </span>
              )}
              {opt.label}
            </button>
          );
        })}
      </div>

      {hint && (
        <p
          className="text-xs text-charcoal/55"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
